/**
 * Shared column show/hide primitives, used by every list table (Alerts, Goals,
 * Tasks, Reports). One source of truth so the control looks & behaves identically
 * everywhere and future tweaks propagate to all pages at once.
 *
 * - HideableColumn / sanitizeHiddenColumns — the column descriptor + a guard that
 *   drops orphan ids (renamed columns, tampered localStorage) on read.
 * - useHiddenColumns — owns the hidden-set state, persists it per-table to
 *   localStorage, exposes a toggle. Pinned columns (the row's name + Actions) are
 *   simply not listed in the page's column array, so they can never be hidden.
 * - HideColumnButton — the hover/focus-revealed "eye-off" button in a column header.
 * - ColumnVisibilityMenu — the toolbar "Columns" dropdown (restore path).
 */

import { useState, useEffect, useCallback } from "react";
import { Button, cn } from "@provenleap/ui";
import {
  SquareSplitHorizontal,
  EyeSlash,
  CaretDown,
  Check,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";

export interface HideableColumn {
  id: string;
  label: string;
}

/**
 * Coerce a persisted value into a clean list of hidden column ids, keeping only
 * ids that are CURRENTLY hideable. Orphan ids self-heal: dropped on read, the
 * caller re-persists the cleaned array.
 */
export function sanitizeHiddenColumns(
  raw: unknown,
  validIds: readonly string[],
): string[] {
  if (!Array.isArray(raw)) return [];
  const valid = new Set(validIds);
  return raw.filter(
    (x): x is string => typeof x === "string" && valid.has(x),
  );
}

/** Persisted-state format version. Bumping the format (bare array → `{v, hidden}`)
 *  lets a column that NEWLY ships hidden-by-default be applied once even to users
 *  who already have a saved setting (the legacy bare-array branch merges the
 *  default in). After that one-time migration the user's choice is honored exactly. */
const HIDDEN_COLUMNS_VERSION = 1;

/**
 * Per-table hidden-column state, persisted to localStorage under `storageKey`.
 * Reads are sanitized against the supplied column list so stale ids can't linger.
 *
 * `defaultHidden` makes a column ship HIDDEN by default while staying restorable:
 * - First visit (nothing saved) → seeded with `defaultHidden`.
 * - A LEGACY saved value (bare array, pre-versioning) → the `defaultHidden` columns
 *   are MERGED in once (so a newly-default-hidden column hides for existing users
 *   too), preserving their prior hides; the versioned format is then persisted.
 * - A versioned saved value → honored EXACTLY (a user who un-hid the column keeps
 *   it visible; the default is never re-applied).
 */
export function useHiddenColumns(
  storageKey: string,
  columns: readonly HideableColumn[],
  defaultHidden: readonly string[] = [],
) {
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    const validIds = columns.map((c) => c.id);
    try {
      const raw = localStorage.getItem(storageKey);
      // First visit (never persisted) → seed the default-hidden set.
      if (raw === null) return sanitizeHiddenColumns(defaultHidden, validIds);
      const parsed: unknown = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed === "object" &&
        !Array.isArray(parsed) &&
        (parsed as { v?: unknown }).v === HIDDEN_COLUMNS_VERSION &&
        Array.isArray((parsed as { hidden?: unknown }).hidden)
      ) {
        // Current format → honor the user's stored choice EXACTLY (no default merge).
        return sanitizeHiddenColumns((parsed as { hidden: unknown[] }).hidden, validIds);
      }
      // Legacy bare-array (or unknown) → one-time migration: merge the
      // default-hidden columns into the prior hides; re-persisted as the new format.
      const legacy = Array.isArray(parsed) ? parsed : [];
      return sanitizeHiddenColumns([...legacy, ...defaultHidden], validIds);
    } catch {
      return sanitizeHiddenColumns(defaultHidden, validIds);
    }
  });

  useEffect(() => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({ v: HIDDEN_COLUMNS_VERSION, hidden: hiddenColumns }),
    );
  }, [storageKey, hiddenColumns]);

  const toggleColumn = useCallback((id: string) => {
    setHiddenColumns((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  }, []);

  return { hiddenColumns, toggleColumn };
}

/**
 * Hover/focus-revealed "hide this column" button for a column header. The parent
 * <th> must carry the `group` class. Uses `.touch-target-sm` to escape the global
 * 44px touch-target min. Stops propagation so it never triggers the header sort.
 */
export function HideColumnButton({
  id,
  label,
  onHide,
}: {
  id: string;
  label: string;
  onHide?: (id: string) => void;
}) {
  if (!onHide) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onHide(id);
      }}
      className="touch-target-sm shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 cursor-pointer"
      aria-label={`Hide ${label} column`}
      title={`Hide ${label} column`}
    >
      <EyeSlash size={13} weight="bold" />
    </button>
  );
}

/**
 * Toolbar "Columns" dropdown — a checkbox per hideable column (checked = visible).
 * The restore path for columns hidden via the header buttons. `align` controls
 * which edge the popover anchors to ("left" by default; use "right" when the
 * button sits on the right side of the toolbar).
 */
export function ColumnVisibilityMenu({
  columns,
  hiddenColumns,
  onToggleColumn,
  align = "left",
}: {
  columns: readonly HideableColumn[];
  hiddenColumns: string[];
  onToggleColumn: (id: string) => void;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        variant="outline"
        className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
        style={{ border: "0.5px solid var(--hairline)" }}
        onClick={() => setOpen(!open)}
        aria-label="Columns"
      >
        <SquareSplitHorizontal size={16} className="mr-1" />
        Columns
        <CaretDown size={12} className="ml-1" />
      </Button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />

            {/* Dropdown panel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className={cn(
                "absolute top-full mt-2.5 z-50 w-56 bg-popover rounded-2xl shadow-md p-2",
                align === "right" ? "right-0" : "left-0",
              )}
              style={{ border: "0.5px solid var(--hairline)" }}
            >
              {/* Arrow pointing up */}
              <div
                className={cn(
                  "absolute -top-[5px] w-2.5 h-2.5 bg-popover rotate-45 z-10",
                  align === "right" ? "right-6" : "left-6",
                )}
                style={{
                  borderLeft: "0.5px solid var(--hairline)",
                  borderTop: "0.5px solid var(--hairline)",
                }}
              />
              <div className="max-h-[260px] overflow-y-auto pt-1 pb-1">
                {columns.map((col) => {
                  const isVisible = !hiddenColumns.includes(col.id);
                  return (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => onToggleColumn(col.id)}
                      aria-pressed={isVisible}
                      className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                    >
                      <span
                        className={cn(
                          "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                          isVisible && "bg-primary border-primary text-white",
                        )}
                      >
                        {isVisible && <Check size={12} />}
                      </span>
                      {col.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
